import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { VehiclePackageData } from "../../models/Vehicle";
import Modal from "../../layouts/Modal";
// import Modal from wherever you have it

// #region Helpers to parse and structure UKVD data for UI
type KeyValueItem = {
  key: string;   // full path, e.g. "motDetails.MotTestDetailsList[7].TestDate"
  value: string; // primitive as string
};

function flattenObjectToKeyValues(
  obj: unknown,
  prefix = ""
): KeyValueItem[] {
  const results: KeyValueItem[] = [];

  if (obj === null || typeof obj !== "object") {
    if (prefix) {
      results.push({ key: prefix, value: String(obj) });
    }
    return results;
  }

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const newPrefix = prefix ? `${prefix}[${index}]` : `[${index}]`;
      if (item === null || typeof item !== "object") {
        results.push({ key: newPrefix, value: String(item) });
      } else {
        results.push(...flattenObjectToKeyValues(item, newPrefix));
      }
    });
    return results;
  }

  Object.entries(obj).forEach(([k, v]) => {
    const newPrefix = prefix ? `${prefix}.${k}` : k;
    if (v === null || typeof v !== "object") {
      results.push({ key: newPrefix, value: String(v) });
    } else {
      results.push(...flattenObjectToKeyValues(v, newPrefix));
    }
  });

  return results;
}

function parseJsonSafe(json: string | null | undefined) {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getAllVehicleKeyValues(vehiclePackageData: VehiclePackageData): KeyValueItem[] {
  const sources: { label: string; json: string }[] = [
    { label: "modelDetails", json: vehiclePackageData.rawModelDetailsJson },
    { label: "vehicleDetails", json: vehiclePackageData.rawVehicleDetailsJson },
    { label: "motDetails", json: vehiclePackageData.rawMotDetailsJson },
    { label: "tyreDetails", json: vehiclePackageData.rawTyreDetailsJson },
    { label: "valuationDetails", json: vehiclePackageData.rawValuationDetailsJson },
    { label: "imageDetails", json: vehiclePackageData.rawImageDetailsJson },
  ];

  return sources.flatMap(({ label, json }) => {
    const parsed = parseJsonSafe(json);
    if (!parsed) return [];
    return flattenObjectToKeyValues(parsed, label);
  });
}

// --- Pretty-name helpers ---

function prettifyLabel(raw: string): string {
  // Turn SomethingLikeThis[7] into "Something Like This 7"
  const noBrackets = raw.replace(/\[(\d+)\]/g, " $1");
  const withSpaces = noBrackets
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();

  return withSpaces
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

function getSectionLabel(sectionKey: string): string {
  // e.g. "modelDetails" -> "Model Details"
  return prettifyLabel(sectionKey);
}

function getFieldLabel(lastSegment: string): string {
  return prettifyLabel(lastSegment);
}

function getGroupLabel(groupKey: string): string {
  // groupKey e.g. "MotTestDetailsList[7]" or "KeeperChangeList[1]"
  const parts = groupKey.split(".");
  const last = parts[parts.length - 1];
  return prettifyLabel(last);
}

type UiItem = {
  id: string;        // full key (for React key + tooltip)
  label: string;     // last segment, pretty
  value: string;
};

type UiGroup = {
  id: string;        // e.g. "MotTestDetailsList[7]" or ""
  label?: string;    // pretty heading ("Mot Test Details List 7"), undefined = no subheading
  items: UiItem[];
};

type UiSection = {
  id: string;        // "modelDetails"
  label: string;     // "Model Details"
  groups: UiGroup[];
};

function buildUiSections(keyValues: KeyValueItem[]): UiSection[] {
  const sectionMap = new Map<string, { label: string; groups: Map<string, UiGroup> }>();

  keyValues.forEach(({ key, value }) => {
    const segments = key.split(".");
    if (segments.length === 0) return;

    const sectionKey = segments[0]; // modelDetails / vehicleDetails / motDetails...
    const section =
      sectionMap.get(sectionKey) ||
      (() => {
        const s = {
          label: getSectionLabel(sectionKey),
          groups: new Map<string, UiGroup>(),
        };
        sectionMap.set(sectionKey, s);
        return s;
      })();

    const lastSegment = segments[segments.length - 1];
    const middleSegments = segments.slice(1, -1); // stuff between section and field

    // For things like motDetails.MotTestDetailsList[7].TestDate,
    // middleSegments = ["MotTestDetailsList[7]"]
    const groupKey = middleSegments.join("."); // "" means "no subgroup"

    const group =
      section.groups.get(groupKey) ||
      (() => {
        const g: UiGroup = {
          id: groupKey,
          label: groupKey ? getGroupLabel(groupKey) : undefined,
          items: [],
        };
        section.groups.set(groupKey, g);
        return g;
      })();

    group.items.push({
      id: key,
      label: getFieldLabel(lastSegment),
      value,
    });
  });

  // Convert sectionMap -> UiSection[]
  return Array.from(sectionMap.entries()).map(([id, sec]) => ({
    id,
    label: sec.label,
    groups: Array.from(sec.groups.values()),
  }));
}
// #endregion

export default function UkvdLookup({
  children,
  registerName,
  displayName,
  setValue,
  vehiclePackageData,
}: {
  children: React.ReactNode;
  registerName: string;
  displayName: string;
  setValue: any;
  vehiclePackageData: VehiclePackageData | null;
}) {
  const [open, setOpen] = useState(false);
  const [openSectionId, setOpenSectionId] = useState<string | null>(null);

  
  const sections = useMemo(() => {
    const keyValues = getAllVehicleKeyValues(vehiclePackageData ?? {} as VehiclePackageData);
    return buildUiSections(keyValues);
  }, [vehiclePackageData]);

  const getGroupDepthClasses = (groupId: string) => {
    if (!groupId) return "";
    const depth = groupId.split(".").length;
    if (depth === 1) return "border-l-2 border-primary-600 pl-5";
    if (depth === 2) return "border-l-2 border-primary-400 pl-5 ml-5";
    return "border-l-2 border-primary-200 pl-5 ml-10";
  };
  
  const handleItemClick = (itemValue: string) => {
    if (registerName) {
      setValue(registerName, itemValue);
    }
    setOpen(false);
  };
  
  if (!vehiclePackageData) return <>{children}</>;

  return (
    <div className="flex items-end space-x-2">
      <Modal open={open} setOpen={setOpen} width="max-w-3xl">
        <div className="mb-4">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-2">
            {displayName} UKVD Lookup
          </h3>

          <div className="max-h-[32rem] overflow-auto border rounded-md divide-y">
            {sections.map((section) => {
              const isOpen = openSectionId === section.id;
              return (
                <div key={section.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-4 py-2 text-left bg-primary-500 hover:bg-primary-600"
                    onClick={() =>
                      setOpenSectionId(isOpen ? null : section.id)
                    }
                  >
                    <span className="font-semibold text-sm text-white">
                      {section.label}
                    </span>
                    <span className="text-xs text-gray-50 ml-2">
                      {section.groups.reduce(
                        (sum, g) => sum + g.items.length,
                        0
                      )}{" "}
                      fields
                    </span>
                  </button>

                  {isOpen && (
                    <div className="px-4 py-3 space-y-3">
                      {section.groups.map((group) => (
                        // <div key={group.id || "default"}>
                        <div
                          key={group.id || "default"}
                          className={getGroupDepthClasses(group.id)}
                        >
                          {group.label && (
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                              {group.label}
                            </div>
                          )}

                          <div className="space-y-1">
                            {group.items.map((item) => (
                              <button
                                key={item.id}
                                type="button"
                                title={item.id} // full path on hover
                                className="w-full grid grid-cols-2 gap-3 text-sm text-left px-2 py-1 rounded hover:bg-gray-100 cursor-pointer"
                                onClick={() => handleItemClick(item.value)}
                              >
                                <span className="font-medium truncate">
                                  {item.label}
                                </span>
                                <span className="text-gray-700 truncate">
                                  {item.value}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Modal>

      {children}

      <button
        className="inline-flex rounded-md border border-transparent bg-primary-600 
        px-3 py-2 text-sm font-medium leading-4 
        text-white shadow-sm hover:bg-primary-700"
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <MagnifyingGlassIcon className="h-5 w-5" />
      </button>
    </div>
  );
}
